import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useLatest } from 'react-use'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { reatomEditWallet } from '@root/features/wallet'
import { Drawer, Frame, SaveIcon } from '@root/shared/ui-kit'

import type { WalletModel } from '@root/entities/wallet/atoms'
import type { DrawerProps } from '@root/shared/ui-kit'

type WalletRenameDrawerProps = Pick<DrawerProps, 'onOpenChange' | 'open'> & {
  wallet: WalletModel
}

type Values = {
  name: string
}

export const WalletRenameDrawer: React.FC<WalletRenameDrawerProps> = ({
  open,
  onOpenChange,
  wallet,
}) => {
  const { t } = useTranslation()
  const editModel = useMemo(
    // eslint-disable-next-line @reatom/unit-naming-rule
    () => reatomEditWallet({ wallet }),
    [wallet],
  )
  const [requestState] = useAtom(editModel)
  const editWallet = useAction(editModel.execute)
  const onOpenChangeRef = useLatest(onOpenChange)
  const [initialName] = useAtom(wallet.name)
  const { handleSubmit, control, formState } = useForm<Values>({
    defaultValues: {
      name: initialName,
    },
    mode: 'all',
  })

  useEffect(() => {
    if (AsyncStateN.isSuccess(requestState)) {
      onOpenChangeRef.current?.(false)
    }
  }, [onOpenChangeRef, requestState])

  const handleSubmitInternal = handleSubmit((values) => {
    editWallet({ name: values.name })
  })

  return (
    <Root
      open={open}
      onOpenChange={onOpenChange}
      title={t('wallet.rename_wallet')}
    >
      <Content onSubmit={handleSubmitInternal}>
        <FrameStyled>
          <Controller
            control={control}
            name="name"
            rules={{ minLength: 2, required: true }}
            render={({ field }) => <NativeInput {...field} />}
          />
        </FrameStyled>
        <Button
          radius="rounded"
          size="m"
          color="primary"
          busy={requestState.loading}
          disabled={!formState.isValid}
          fullWidth
          iconLeft={<SaveIcon />}
          type="submit"
        >
          {t('common.save')}
        </Button>
      </Content>
    </Root>
  )
}

const Root = styled(Drawer)`
  height: var(--max-drawer-height);
  display: grid;
  grid-template-rows: 1fr;
`

const Content = styled.form`
  padding-inline: 12px;
  padding-bottom: calc(var(--safe-area-inset-bottom) + 12px);

  display: grid;
  grid-template-rows: 1fr auto;
`

const NativeInput = styled.input`
  background: transparent;
  outline: none;
  border: none;

  font-weight: 600;
  font-size: 14px;
  color: var(--text-12);
  width: 100%;
  padding-block: 8px;
`

const FrameStyled = styled(Frame)`
  margin-bottom: 24px;
  height: 48px;
  padding-inline: 16px;

  display: flex;
  align-items: center;

  border-radius: 12px;
  border: 1px solid var(--backgroundA-3);
  background: var(--appearance-1, #000);
`
